import { LoadingState } from '../ui';
﻿import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { subscribeToChanges } from '../lib/realtimeSubscriptions';
import notify from "../lib/notify";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import TicketConversation from "../components/TicketConversation";
import { Button as FcButton, Card as FcCard } from '../ui';

export default function MyTickets({ session, userRole, username }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [expandedTicketId, setExpandedTicketId] = useState(null);

  const markTicketsAsRead = async () => {
    try {
      // Mark all user's tickets as read when they view the page
      const { data, error } = await supabase
        .from("support_tickets")
        .update({ read_by_user: true })
        .eq("user_id", session.user.id)
        .eq("read_by_user", false)
        .select();

      if (error) {
        return [];
      } else {
        return data || [];
      }
    } catch (err) {
      return [];
    }
  };

  const fetchTickets = async () => {
    try {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      notify("Failed to load tickets", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      await fetchTickets();
      const result = await markTicketsAsRead();
      // Force a page event to trigger sidebar refetch
      if (result && result.length > 0) {
        // Dispatch custom event that sidebar can listen to
        window.dispatchEvent(new CustomEvent('tickets-marked-read'));
        // Also wait longer for real-time to propagate
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    };
    
    initialize();
    
    // Set up real-time subscription
    const unsubscribe = subscribeToChanges(supabase, 'support_tickets_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_tickets',
          filter: `user_id=eq.${session.user.id}`
        },
        (payload) => {
          fetchTickets();
        }
      );

    return () => {
      unsubscribe();
    };
  }, [session.user.id]);

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-100 text-blue-800",
      in_progress: "bg-yellow-100 text-yellow-800",
      resolved: "bg-green-100 text-green-800",
      closed: "bg-gray-100 text-gray-800",
    };

    const labels = {
      new: "New",
      in_progress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: "bg-gray-100 text-gray-700",
      medium: "bg-blue-100 text-blue-700",
      high: "bg-orange-100 text-orange-700",
      urgent: "bg-red-100 text-red-700",
    };

    const labels = {
      low: "Low",
      medium: "Medium",
      high: "High",
      urgent: "Urgent",
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[priority]}`}>
        {labels[priority]}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      bug: "🐛",
      feature: "💡",
      question: "❓",
      account: "👤",
      other: "📝",
    };
    return icons[category] || "📝";
  };

  const filteredTickets = tickets.filter((ticket) => {
    if (activeFilter === "all") return true;
    return ticket.status === activeFilter;
  });

  const getFilterCount = (status) => {
    if (status === "all") return tickets.length;
    return tickets.filter((t) => t.status === status).length;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toggleTicketExpansion = (ticketId) => {
    setExpandedTicketId(expandedTicketId === ticketId ? null : ticketId);
  };

  if (loading) {
    return (
      <div className="flex">
        <SidebarNavbar
          handleLogout={async () => {
            await supabase.auth.signOut();
            navigate("/login");
          }}
          userRole={userRole}
        />
        <div role="main" id="fc-main-content" className="flex-1 md:ml-64 fc-workspace">
          <div className="min-h-screen bg-surface-canvas py-8 px-4 md:px-8 pb-20 md:pb-8">
            <div className="flex justify-center items-center h-64">
              <LoadingState label="Loading tickets..." />
            </div>
          </div>
        </div>
        <BottomNavbar
          userRole={userRole}
          username={username}
          handleLogout={async () => {
            await supabase.auth.signOut();
            navigate("/login");
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex">
      <SidebarNavbar
        handleLogout={async () => {
          await supabase.auth.signOut();
          navigate("/login");
        }}
        userRole={userRole}
      />
      <div role="main" id="fc-main-content" className="flex-1 md:ml-64 fc-workspace">
        <div className="min-h-screen bg-surface-canvas py-8 px-4 md:px-8 pb-20 md:pb-8">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-ui-green mb-2">My Support Tickets</h1>
            <p className="text-content-secondary">Track the status of your support requests</p>
          </div>

          {/* Actions */}
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <FcButton variant="tertiary" type="submit"
              onClick={() => navigate("/support")}
              className=""
            >
              + Create New Ticket
            </FcButton>

            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: "all", label: "All" },
                { value: "new", label: "New" },
                { value: "in_progress", label: "In Progress" },
                { value: "resolved", label: "Resolved" },
                { value: "closed", label: "Closed" },
              ].map((filter) => (
                <FcButton variant="primary" type="submit" aria-pressed={activeFilter === filter.value}
                  key={filter.value}
                  onClick={() => setActiveFilter(filter.value)}
                  className={`${
                    activeFilter === filter.value
                      ? ""
                      : "border"
                  }`}
                >
                  {filter.label} ({getFilterCount(filter.value)})
                </FcButton>
              ))}
            </div>
          </div>

          {/* Tickets List */}
          {filteredTickets.length === 0 ? (
            <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-md p-12 text-center">
              <div className="text-6xl mb-4">🎫</div>
              <h3 className="text-xl font-semibold text-ui-green mb-2">
                {activeFilter === "all" ? "No tickets yet" : `No ${activeFilter.replace("_", " ")} tickets`}
              </h3>
              <p className="text-content-secondary mb-6">
                {activeFilter === "all"
                  ? "You haven't submitted any support tickets yet."
                  : `You don't have any tickets with this status.`}
              </p>
              {activeFilter === "all" && (
                <FcButton variant="primary" type="submit"
                  onClick={() => navigate("/support")}
                  className=""
                >
                  Create Your First Ticket
                </FcButton>
              )}
            </FcCard>
          ) : (
            <div className="space-y-4">
              {filteredTickets.map((ticket) => (
                <FcCard as="div" padding="none"
                  key={ticket.id}
                  className={`rounded-lg shadow-md overflow-hidden transition-all hover:shadow-lg ${
                    !ticket.read_by_user
                      ? "bg-surface-canvas border-2 border-ui-border"
                      : "bg-surface-card"
                  }`}
                >
                  {/* Ticket Header */}
                  <FcButton variant="secondary" type="button" aria-expanded={expandedTicketId === ticket.id} aria-label={`Ticket: ${ticket.subject}`}
                    onClick={() => toggleTicketExpansion(ticket.id)}
                    className="fc-ticket-disclosure w-full text-left"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          {!ticket.read_by_user && (
                            <span className="inline-block w-2 h-2 bg-action-primary rounded-full mt-2 animate-pulse flex-shrink-0" />
                          )}
                          <span className="text-2xl">{getCategoryIcon(ticket.category)}</span>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-ui-green mb-1">
                              {ticket.subject}
                            </h3>
                            {ticket.admin_notes && !ticket.read_by_user && (
                              <p className="text-sm text-action-primary font-medium mb-2">
                                ✨ New response from support team
                              </p>
                            )}
                            <div className="flex flex-wrap gap-2 mb-2">
                              {getStatusBadge(ticket.status)}
                              {getPriorityBadge(ticket.priority)}
                            </div>
                            <p className="text-sm text-content-secondary">
                              Created: {formatDate(ticket.created_at)}
                              {ticket.updated_at !== ticket.created_at && (
                                <> • Updated: {formatDate(ticket.updated_at)}</>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Expand Icon */}
                      <div className="flex items-center">
                        <svg
                          className={`w-6 h-6 text-ui-green transition-transform ${
                            expandedTicketId === ticket.id ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </FcButton>

                  {/* Expanded Details */}
                  {expandedTicketId === ticket.id && (
                    <div className="border-t border-ui-border p-6 bg-surface-card">
                      <div className="space-y-4">
                        {/* Conversation */}
                        <div>
                          <h4 className="text-sm font-semibold text-ui-green mb-4">
                            💬 Conversation
                          </h4>
                          <TicketConversation 
                            ticketId={ticket.id} 
                            userId={session.user.id}
                            isAdmin={false}
                            initialMessage={ticket.message}
                            createdAt={ticket.created_at}
                          />
                        </div>

                        {/* Resolution Info */}
                        {ticket.resolved_at && (
                          <div className="text-sm text-content-secondary pt-4 border-t">
                            <strong>Resolved:</strong> {formatDate(ticket.resolved_at)}
                          </div>
                        )}

                        {/* Ticket ID */}
                        <div className="text-xs text-content-secondary pt-2 border-t">
                          Ticket ID: {ticket.id}
                        </div>
                      </div>
                    </div>
                  )}
                </FcCard>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    <BottomNavbar
      userRole={userRole}
      username={username}
      handleLogout={async () => {
        await supabase.auth.signOut();
        navigate("/login");
      }}
    />
  </div>
);
}
